import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errText } from "@/lib/api";
import { GraduationCap, Loader2, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(username, password);
      toast.success(`Selamat datang, ${u.name}`);
      navigate(u.role === "admin" ? "/dashboard" : "/portal");
    } catch (err) {
      setError(errText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #25499B 0%, transparent 42%), radial-gradient(circle at 80% 75%, #F16522 0%, transparent 42%)" }} />
        <div className="relative flex items-center gap-3">
          <img src="/favicon.png" alt="Logo" className="h-11 w-11 rounded-xl object-cover" />
          <span className="font-heading text-xl font-extrabold text-white">Web Monitoring Pengembangan Karyawan</span>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl font-extrabold text-white leading-tight">
            Monitoring Program<br />Pengembangan Karyawan
          </h1>
          <p className="mt-4 text-slate-300 text-base max-w-md leading-relaxed">
            Catat kehadiran, jalankan kuis otomatis berbasis AI, dan pantau rekap penilaian terbobot setiap karyawan dalam satu platform.
          </p>
          <div className="mt-8 flex gap-6">
            <div className="flex items-center gap-2 text-slate-300 text-sm"><ShieldCheck className="h-5 w-5 text-orange-400" /> Penilaian Terbobot</div>
            <div className="flex items-center gap-2 text-slate-300 text-sm"><Users className="h-5 w-5 text-blue-400" /> Multi-Peran</div>
          </div>
        </div>
        <p className="relative text-xs text-slate-500">Batas kelulusan nilai akhir ≥ 7.0 dari skala 0–10</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/favicon.png" alt="Logo" className="h-11 w-11 rounded-xl object-cover" />
            <span className="font-heading text-lg font-extrabold text-slate-900">Web Monitoring Pengembangan Karyawan</span>
          </div>
          <h2 className="font-heading text-3xl font-extrabold text-slate-900">Masuk ke Akun</h2>
          <p className="mt-2 text-slate-500 text-sm">Masuk dengan Nomor ID Karyawan Anda.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nomor ID Karyawan</label>
              <input
                data-testid="login-email-input"
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="mis. EMP001"
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Kata Sandi</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
              />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Masuk
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}
